import React from 'react';
import _ from 'lodash';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import Editor from 'ckeditor5-build-full';
import { Navigate, Input, Message } from '../components';
import {
    __,
    formatAmount,
    formatCurrency,
    getCurrency,
    request,
    toNumber,
    isValidEan,
    getCurrencyPrice
} from '../functions';
import '../assets/styles/product.css';
import {ButtonBase, Chip, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tooltip} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';

/**
 * Produkt komponenta.
 */
class Product extends Navigate {
    static defaultProps = {
        id: 0,
        user: {},
    };

    state = {
        tab: 'translations',
        subtab: '',
        data: {},
        settings: {},
        loading: false,
        languages: [],
        currencies: [],
        loadingsAi: {},
        loadingsSave: {},
        loadingsRemove: {},
        ai_commands: [],
    };

    ai_commands_interval = null;
    ai_commands_interval_counter = 0;

    componentDidMount() {
        this.fetch();
    }

    componentWillUnmount() {
        this.clearAiCommandsInterval();
    }

    fetch(force_subtab = '') {
        const { id, onClose, setTitle, showSnackbar } = this.props;
        const { tab } = this.state;

        // Nacitame data produktu
        request(`/products-full/view/${id}`).then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                // Produkt neexistuje
                showSnackbar('error', __('Produkt neexistuje'));
                onClose();
                return;
            }

            let languages = [];
            let currencies = [];
            let subtab = '';

            if (!_.isEmpty(data.product.translations)) {
                // Nastavime title
                setTitle(_.truncate(data.product.translations[0].name, { length: this.isMobile() ? 32 : 64 }));

                _.each(data.product.translations, translation => {
                    if (!_.includes(languages, translation.language)) {
                        languages = [ ...languages, translation.language ];
                    }
                });

                if (languages.length > 1) {
                    subtab = force_subtab !== '' ? force_subtab : languages[0];
                }
            }

            if (!_.isEmpty(data.product.pricelists)) {
                _.each(data.product.pricelists, pricelist => {
                    if (!_.includes(currencies, pricelist.currency)) {
                        currencies = [ ...currencies, pricelist.currency ];
                    }
                });
            }

            this.setState({ data, languages, currencies, subtab, ...this.getAiCommands(data.ai_commands, tab, subtab) });

            if (this.hasProcessingAiCommand(data.ai_commands)) {
                this.setAiCommandsInterval();
            }
        });
    }

    onChangeSetting(name, value) {
        const { settings } = this.state;

        this.setState({ settings: { ...settings, [name]: value } });
    }

    onChangeTab(tab) {
        let { subtab, languages, currencies, data } = this.state;

        switch (tab) {
            case 'translations':
            case 'parameters':
                subtab = languages.length > 1 ? languages[0] : '';
                break;

            case 'pricelists':
                subtab = currencies.length > 1 ? currencies[0] : '';
                break;
        }

        this.setState({ tab, subtab, ...this.getAiCommands(data.ai_commands, tab, subtab) });
    }

    onChangeSubTab(subtab) {
        const { tab, data } = this.state;

        this.setState({ subtab, ...this.getAiCommands(data.ai_commands, tab, subtab) });
    }

    renderValue(value) {
        return !_.isEmpty(value) ? value : '-';
    }

    showHtmlEditor(value) {
        return value.toString().indexOf('</p>') !== -1 || value.toString().indexOf('</ul>') !== -1;
    }

    async generateAi(field, aiFunctionSlug) {
        const { id } = this.props;
        const { loadingsAi, subtab } = this.state;

        this.setState({ loadingsAi: { ...loadingsAi, [field]: field }, loadingsSave: {}, loadingsRemove: {} });

        let variable_inputs = [];
        let variables = [];
        let force_input = '';

        if (aiFunctionSlug === this.getAiTranslateFunctionSlug()) {
            variable_inputs = [this.getAiTranslateFieldByField(field)];
            variables = { language: subtab };
        } else if (
            aiFunctionSlug !== 'GenerateProductNameByEan'
            && aiFunctionSlug !== 'GenerateProductShortDescriptionByEan'
            && aiFunctionSlug !== 'GenerateProductDescriptionByEan'
        ) {
            switch (aiFunctionSlug) {
                case 'GenerateProductShortDescription':
                    variable_inputs = [
                        'product_name',
                        'product_parameters',
                        'product_description',
                    ];
                    break;

                case 'GenerateProductDescription':
                    variable_inputs = [
                        'product_name',
                        'product_parameters',
                        'product_short_description',
                    ];
                    break;

                case 'GenerateProductSeoTitle':
                case 'GenerateProductSeoDescription':
                    variable_inputs = [
                        'product_name',
                        'product_parameters',
                        'product_short_description',
                        'product_description',
                    ];
                    break;
            }
        } else if (aiFunctionSlug === 'GenerateProductNameByEan') {
            force_input = this.getTranslationFieldValue(this.getSelectedTranslation(subtab), 'name').value;
        }

        let created_ai_command = false;

        await request(`/products-full/multiAi/${aiFunctionSlug}/${id}`, {
            variable_inputs,
            variables,
            force_input,
            save_reference: true,
        }, 'POST').then(response => {
            const { status } = response.data;

            if (status === 'error') {
                this.showSnackbar('error', __('Nepodarilo sa vytvoriť AI úlohu'));

                this.setState({ loadingsAi: {} });
                return;
            }

            created_ai_command = true;
        });

        if (created_ai_command) {
            this.setAiCommandsInterval();
        }
    }

    fetchAiCommands(force = false) {
        const { tab, subtab } = this.state;
        const { id } = this.props;

        this.ai_commands_interval_counter++;

        if (!force && (this.ai_commands_interval_counter > 30 || tab !== 'translations')) {
            this.clearAiCommandsInterval();
            return;
        }

        request(`/products-full/getAiCommands/${id}`).then(response => {
            const { status, data } = response.data;

            if (status === 'success') {
                let new_state = this.getAiCommands(data.ai_commands, tab, subtab);
                new_state = { ...new_state, data: { ...this.state.data, ai_commands: data.ai_commands } };

                if (!this.hasProcessingAiCommand(data.ai_commands)) {
                    new_state = { ...new_state, loadingsAi: {} };

                    this.clearAiCommandsInterval();
                }

                this.setState(new_state);
            }
        });
    }

    setAiCommandsInterval() {
        this.clearAiCommandsInterval();

        this.ai_commands_interval = setInterval(() => {
            this.fetchAiCommands();
        }, 20000);
    }

    clearAiCommandsInterval() {
        if (this.ai_commands_interval !== null) {
            clearInterval(this.ai_commands_interval);

            this.ai_commands_interval = null;
            this.ai_commands_interval_counter = 0;
        }
    }

    getSaveFieldsWithoutLanguage() {
        const { data } = this.state;

        return _.reduce(data.save_fields, (result, item) => {
            const language_field = /^([a-z]{2})\.(.*)$/.exec(item);

            if (
                language_field !== null
                && language_field.length === 3
            ) {
                // Remove language prefix
                return [ ...result, language_field[2] ];
            }

            return [ ...result, item ];
        }, []);
    }

    getMetaFieldName(field) {
        if (field === 'name') {
            return '';
        }

        const { data, subtab } = this.state;
        const saveFieldsWithoutLanguage = this.getSaveFieldsWithoutLanguage();
        const allowMeta = _.includes(saveFieldsWithoutLanguage, 'meta.{meta_key}');

        if (!allowMeta || _.includes(saveFieldsWithoutLanguage, this.convertSaveField(field))) {
            return '';
        }

        const setting_meta_key = `meta_data_product_${field}`;
        let meta_suffix = '';

        if (this.isTranslate(subtab)) {
            meta_suffix = `_${subtab}`;
        }

        return _.has(data.eshop_settings, setting_meta_key) ? `meta.${data.eshop_settings[setting_meta_key]}${meta_suffix}` : '';
    }

    convertSaveField(field) {
        switch (field) {
            case 'name':
                return 'title';

            case 'short_description':
                return 'short_description';

            case 'description':
                return 'long_description';

            case 'seo_title':
                return 'seo_title';

            case 'seo_description':
                return 'seo_description';
        }

        return '';
    }

    getSaveField(field) {
        const { user } = this.props;
        const { data, subtab } = this.state;

        let save_field = this.convertSaveField(field);
        const meta_field = this.getMetaFieldName(field);

        if (meta_field !== '') {
            return meta_field;
        }

        if (save_field === '') {
            return '';
        }

        const language_save_fields = _.reduce(data.save_fields, (result, item) => {
            const language_field = /^([a-z]{2})\.(.*)$/.exec(item);

            if (
                language_field !== null
                && language_field.length === 3
                && language_field[2] === save_field
            ) {
                return { ...result, [language_field[1]]: item };
            }

            return result;
        }, {});

        if (!_.isEmpty(language_save_fields)) {
            let language = subtab;

            if (language === '') {
                language = user.settings.market;
            }

            if (_.has(language_save_fields, language)) {
                return language_save_fields[language];
            }
        }

        if (subtab === '') {
            // Without language
            return save_field;
        }

        return '';
    }

    async save(field, ai_command) {
        const { loadingsSave, subtab } = this.state;

        this.setState({ loadingsSave: { ...loadingsSave, [field]: field } });

        const save_field = this.getSaveField(field);

        if (save_field === '') {
            this.showSnackbar('error', __('V eshope neexistuje údaj pre uloženie'));
            return;
        }

        const ai_command_id = ai_command._matchingData.AiCommands.id;
        const ids = ai_command._matchingData.AiCommandItems.id;
        let force_output = this.getTranslationFieldValue(this.getSelectedTranslation(subtab), field).value;

        if (this.isDemo()) {
            force_output = '';
        }

        await request(`/ai-command-items/multiSave/${ai_command_id}/${save_field}/${ids}`, { force_output }, 'POST').then(response => {
            const { data, status } = response.data;

            if (status === 'error' || !_.isEmpty(data.errors)) {
                this.showSnackbar('error', __('Nepodarilo sa uložiť v eshope'));
                return;
            }

            this.showSnackbar('success', __('Uložené'));
        });

        this.setState({ data: {}, settings: {}, loadingsAi: {}, loadingsSave: {} });
        this.fetch(subtab);
    }

    async remove(field, ai_command_id) {
        const { loadingsRemove, subtab } = this.state;

        this.setState({ loadingsRemove: { ...loadingsRemove, [field]: field } });

        await request(`/ai-commands/delete/${ai_command_id}`).then(response => {});

        this.setState({ data: {}, settings: {}, loadingsRemove: {} });
        this.fetch(subtab);
    }

    hasProcessingAiCommand(ai_commands) {
        let result = false;

        _.each(ai_commands, ai_command => {
            if (_.includes(['waiting', 'processing'], ai_command._matchingData.AiCommandItems.status)) {
                result = true;
            }
        });

        return result;
    }

    getAiCommands(ai_commands, tab, subtab) {
        const isTranslate = this.isTranslate(subtab);
        let settings = {};

        _.each(['name', 'short_description', 'description', 'seo_title', 'seo_description'], field => {
            const aiFunctionSlugs = isTranslate ? [this.getAiTranslateFunctionSlug()] : this.getAiFunctionsByField(field);
            const aiCommand = this.getAiCommand(ai_commands, aiFunctionSlugs, field, subtab);

            if (aiCommand !== null && aiCommand._matchingData.AiCommandItems.status === 'processed') {
                settings = { ...settings, [field]: aiCommand._matchingData.AiCommandItems.output };
            }
        });

        return {
            ai_commands,
            settings,
        };
    }

    getAiCommand(ai_commands, aiFunctionSlugs, field, language) {
        let command = null;
        let has_last_saved = false;

        if (this.isEshopChildWithOneLanguage() && this.state.data.child_eshop_language !== '') {
            language = this.state.data.child_eshop_language;
        }

        _.each(ai_commands, ai_command => {
            if (
                _.includes(aiFunctionSlugs, ai_command.slug)
                && command === null
                && !has_last_saved
            ) {
                // Last not saved command
                const is_saved = toNumber(ai_command._matchingData.AiCommandItems.saved) === 1;

                if (aiFunctionSlugs[0] === this.getAiTranslateFunctionSlug()) {
                    // Translate compare language and translated field
                    const translate_field = this.getAiTranslateFieldByField(field);

                    if (
                        _.isObject(ai_command._matchingData.AiCommands.settings)
                        && _.has(ai_command._matchingData.AiCommands.settings, 'language')
                        && _.has(ai_command._matchingData.AiCommands.settings, 'translate_field')
                        && ai_command._matchingData.AiCommands.settings.language === language
                        && ai_command._matchingData.AiCommands.settings.translate_field === translate_field
                    ) {
                        if (is_saved) {
                            has_last_saved = true;
                        } else {
                            command = ai_command;
                        }
                    }
                } else {
                    if (is_saved) {
                        has_last_saved = true;
                    } else {
                        command = ai_command;
                    }
                }
            }
        });

        return command;
    }

    getAiTranslateFieldByField(field) {
        switch (field) {
            case 'name':
                return 'product_name';

            case 'short_description':
                return 'product_short_description';

            case 'description':
                return 'product_description';

            case 'seo_title':
                return 'product_seo_title';

            case 'seo_description':
                return 'product_seo_description';
        }

        return '';
    }

    getAiFunctionsByField(field) {
        switch (field) {
            case 'name':
                return [
                    'GenerateProductNameByEan',
                ];

            case 'short_description':
                return [
                    'GenerateProductShortDescriptionByEan',
                    'GenerateProductShortDescription',
                ];

            case 'description':
                return [
                    'GenerateProductDescriptionByEan',
                    'GenerateProductDescription',
                ];

            case 'seo_title':
                return [
                    'GenerateProductSeoTitle',
                ];

            case 'seo_description':
                return [
                    'GenerateProductSeoDescription',
                ];
        }

        return '';
    }

    getAiTranslateFunctionSlug() {
        return 'TranslatesTextIntoTheSelectedLanguage';
    }

    isEshopChildWithOneLanguage() {
        const { data } = this.state;

        return toNumber(data.main_user_eshop_id) > 0 && data.product.translations.length === 1;
    }

    isTranslate(subtab) {
        const { user } = this.props;

        if (this.isEshopChildWithOneLanguage()) {
            return true;
        }

        return subtab !== '' && subtab !== user.settings.market;
    }

    getTranslationFieldValue(selectedTranslation, field, useSettings = true) {
        const { settings, data } = this.state;

        const meta_field = this.getMetaFieldName(field).replace('meta.', '');
        const saveFieldsWithoutLanguage = this.getSaveFieldsWithoutLanguage();
        const inSelectedTranslation = _.includes(saveFieldsWithoutLanguage, this.convertSaveField(field));

        if (_.has(settings, field) && useSettings) {
            return {
                value: settings[field],
                meta: !inSelectedTranslation ? meta_field : '',
            };
        }

        if (inSelectedTranslation) {
            return {
                value: selectedTranslation[field],
                meta: meta_field,
            };
        }

        if (field === 'name') {
            return {
                value: '',
                meta: '',
            };
        }

        if (!_.isEmpty(data.product.meta_data) && _.has(data.product.meta_data, meta_field)) {
            return {
                value: data.product.meta_data[meta_field],
                meta: meta_field,
            };
        }

        return {
            value: '',
            meta: meta_field,
        };
    }

    getSelectedTranslation(subtab) {
        const { data } = this.state;

        let selectedTranslation = null;

        _.map(data.product.translations, translation => {
            if (subtab === '') {
                selectedTranslation = translation;
            }

            if (translation.language === subtab) {
                selectedTranslation = translation;
            }
        });

        return selectedTranslation;
    }

    isValidEan() {
        const { data } = this.state;

        return _.has(data.product, 'ean') && isValidEan(data.product.ean);
    }

    renderAiButtons(field, value, nameValue = '') {
        const { loadingsAi, loadingsSave, loadingsRemove, subtab, ai_commands } = this.state;

        const saveFieldsWithoutLanguage = this.getSaveFieldsWithoutLanguage();
        const allowMeta = _.includes(saveFieldsWithoutLanguage, 'meta.{meta_key}');
        const isTranslate = this.isTranslate(subtab);

        if (field === 'short_description' && !_.includes(saveFieldsWithoutLanguage, 'short_description') && !allowMeta) {
            return null;
        } else if (field === 'description' && !_.includes(saveFieldsWithoutLanguage, 'long_description') && !allowMeta) {
            return null;
        } else if (field === 'seo_title' && !_.includes(saveFieldsWithoutLanguage, 'seo_title') && !allowMeta) {
            return null;
        } else if (field === 'seo_description' && !_.includes(saveFieldsWithoutLanguage, 'seo_description') && !allowMeta) {
            return null;
        }

        let aiFunctionSlugs = [this.getAiTranslateFunctionSlug()];

        if (!isTranslate) {
            aiFunctionSlugs = this.getAiFunctionsByField(field);
        }

        const ai_command = this.getAiCommand(ai_commands, aiFunctionSlugs, field, subtab);

        let aiFunctionSlug = '';

        if (aiFunctionSlugs.length > 1) {
            // First function by EAN
            if (this.isValidEan()) {
                aiFunctionSlug = aiFunctionSlugs[0];
            } else {
                aiFunctionSlug = aiFunctionSlugs[1];
            }
        } else {
            aiFunctionSlug = aiFunctionSlugs[0];
        }

        let isAiLoading = _.has(loadingsAi, field);
        let isSaveLoading = _.has(loadingsSave, field);
        let isRemoveLoading = _.has(loadingsRemove, field);
        let showSave = false;
        let showError = false;
        let errorMessage = '';
        let price = null;
        let ai_command_id = 0;

        if (ai_command !== null) {
            switch (ai_command._matchingData.AiCommandItems.status) {
                case 'waiting':
                case 'processing':
                    isAiLoading = true;
                    break;

                case 'processed':
                    showSave = true;
                    ai_command_id = ai_command._matchingData.AiCommands.id;
                    price = `${getCurrencyPrice(ai_command._matchingData.AiCommands.price).replace('.', ',')} ${formatCurrency(getCurrency())}`;

                    if (!_.isEmpty(ai_command._matchingData.AiCommandItems.error_save)) {
                        showError = true;
                        errorMessage = ai_command._matchingData.AiCommandItems.error_save;
                    }
                    break;

                case 'error':
                    showError = true;
                    errorMessage = ai_command._matchingData.AiCommandItems.error;

                    switch (errorMessage) {
                        case 'Empty credit':
                            errorMessage = __('Nemáte dostatočný kredit');
                            break;
                    }
                    break;
            }
        }

        const disabledGenerateAi = !_.isEmpty(loadingsSave)
            || (!isTranslate && field === 'name' && !/^[0-9]+$/.test(_.toString(value)));
        let disabledSave = (!isSaveLoading && !_.isEmpty(loadingsSave)) || !_.isEmpty(loadingsAi) || !_.isEmpty(loadingsRemove);
        let emptyName = false;

        if (field !== 'name' && _.isEmpty(nameValue)) {
            disabledSave = true;
            emptyName = true;
        }

        return (
            <div className="product-content__panel__tab-content__buttons">
                <ButtonBase
                    onClick={!disabledGenerateAi ? () => this.generateAi(field, aiFunctionSlug) : () => {}}
                    className={`product-content__panel__tab-content__buttons__button-ai ${disabledGenerateAi ? 'product-content__panel__tab-content__buttons__button-disabled' : ''}`}
                >
                    {isAiLoading ? this.renderLoading(16) : <SmartToyIcon />}
                    <span>{isAiLoading
                        ? __('Spracováva sa, zatiaľ môžete generovať dalšie úlohy cez AI')
                        : (isTranslate
                            ? __('Preložiť cez AI')
                            : (field === 'name' && !/^[0-9]+$/.test(_.toString(value)) ? __('Názov musí byť EAN') : __('Vygenerovať cez AI'))
                        )}
                    </span>
                </ButtonBase>
                {showSave ? <ButtonBase
                    onClick={!disabledSave ? () => this.save(field, ai_command) : () => {}}
                    className={`product-content__panel__tab-content__buttons__button-save ${disabledSave ? 'product-content__panel__tab-content__buttons__button-disabled' : ''}`}
                >
                    {isSaveLoading ? this.renderLoading(16) : <SaveIcon />}
                    <span>{isSaveLoading ? null : (emptyName ? __('Názov nie je vyplnený a uložený') : __('Uložiť do eshopu'))}</span>
                </ButtonBase> : null}
                {showSave && toNumber(ai_command_id) > 0 && !isSaveLoading && !isAiLoading ? <ButtonBase
                    onClick={!isRemoveLoading ? () => this.remove(field, ai_command_id) : () => {}}
                    className="product-content__panel__tab-content__buttons__button-remove"
                >
                    {isRemoveLoading ? this.renderLoading(16) : <DeleteIcon />}
                    <span>{isRemoveLoading ? null : __('Zahodiť')}</span>
                </ButtonBase> : null}
                {showError ? <div className="product-content__panel__tab-content__buttons__error">{errorMessage}</div> : null}
                {showSave && !showError && price !== null ? <div className="product-content__panel__tab-content__buttons__price">{__('Cena')}: {price}</div> : null}
            </div>
        );
    }

    renderTab() {
        const { tab, subtab, data } = this.state;

        switch (tab) {
            case 'translations':
                const selectedTranslation = this.getSelectedTranslation(subtab);

                if (selectedTranslation === null) {
                    return null;
                }

                const isValidEan = this.isValidEan();

                const allowMeta = _.includes(this.getSaveFieldsWithoutLanguage(), 'meta.{meta_key}');
                const name = this.getTranslationFieldValue(selectedTranslation, 'name');
                const nameWithoutSettings = this.getTranslationFieldValue(selectedTranslation, 'name', false);
                const short_description = this.getTranslationFieldValue(selectedTranslation, 'short_description');
                const description = this.getTranslationFieldValue(selectedTranslation, 'description');
                const seo_title = this.getTranslationFieldValue(selectedTranslation, 'seo_title');
                const seo_description = this.getTranslationFieldValue(selectedTranslation, 'seo_description');

                const nameButtons = this.renderAiButtons('name', name.value);
                const shortDescriptionButtons = this.renderAiButtons('short_description', short_description.value, nameWithoutSettings.value);
                const descriptionButtons = this.renderAiButtons('description', description.value, nameWithoutSettings.value);
                const seoTitleButtons = this.renderAiButtons('seo_title', seo_title.value, nameWithoutSettings.value);
                const seoDescriptionButtons = this.renderAiButtons('seo_description', seo_description.value, nameWithoutSettings.value);

                return (
                    <>
                        <Message type="warning">{__('Dáta k produktom cez AI viete generovať aj hromadne v zozname produktov.')}</Message>
                        <Input
                            label={__('Názov')}
                            value={name.value}
                            onChange={value => this.onChangeSetting('name', value)}
                        />
                        {nameButtons}
                        {shortDescriptionButtons !== null && this.showHtmlEditor(short_description.value) ? <div className="input__label editor-label">{`${__('Krátky popis')}${allowMeta && short_description.meta !== '' ? ` (meta: ${short_description.meta})` : ''}`}</div> : null}
                        {shortDescriptionButtons !== null ? (this.showHtmlEditor(short_description.value) ? <CKEditor
                            editor={Editor}
                            data={short_description.value}
                            onChange={(event, editor) => this.onChangeSetting('short_description', editor.getData())}
                        /> : <Input
                            label={`${__('Krátky popis')}${allowMeta && short_description.meta !== '' ? ` (meta: ${short_description.meta})` : ''}`}
                            value={short_description.value}
                            onChange={value => this.onChangeSetting('short_description', value)}
                            multiline
                        />) : null}
                        {shortDescriptionButtons}
                        {descriptionButtons !== null && this.showHtmlEditor(description.value) ? <div className="input__label editor-label">{`${__('Dlhý popis')}${allowMeta && description.meta !== '' ? ` (meta: ${description.meta})` : ''}`}</div> : null}
                        {descriptionButtons !== null ? (this.showHtmlEditor(description.value) ? <CKEditor
                            editor={Editor}
                            data={description.value}
                            onChange={(event, editor) => this.onChangeSetting('description', editor.getData())}
                        /> : <Input
                            label={`${__('Dlhý popis')}${allowMeta && description.meta !== '' ? ` (meta: ${description.meta})` : ''}`}
                            value={description.value}
                            onChange={value => this.onChangeSetting('description', value)}
                            multiline
                        />) : null}
                        {descriptionButtons}
                        {seoTitleButtons !== null ? <Input
                            label={`${__('SEO názov')}${allowMeta && seo_title.meta !== '' ? ` (meta: ${seo_title.meta})` : ''}`}
                            value={seo_title.value}
                            onChange={value => this.onChangeSetting('seo_title', value)}
                        /> : null}
                        {seoTitleButtons}
                        {isValidEan && !this.isTranslate(subtab) && short_description.value === '' && description.value === '' ? <div className="product-content__panel__tab-content__info">
                            <InfoIcon />
                            <span>{__('Pre lepší výsledok vygenerujte najprv krátky alebo dlhý popis produktu.')}</span>
                        </div> : null}
                        {seoDescriptionButtons !== null ? <Input
                            label={`${__('SEO popis')}${allowMeta && seo_description.meta !== '' ? ` (meta: ${seo_description.meta})` : ''}`}
                            value={seo_description.value}
                            onChange={value => this.onChangeSetting('seo_description', value)}
                        /> : null}
                        {seoDescriptionButtons}
                        {isValidEan && !this.isTranslate(subtab) && short_description.value === '' && description.value === '' ? <div className="product-content__panel__tab-content__info">
                            <InfoIcon />
                            <span>{__('Pre lepší výsledok vygenerujte najprv krátky alebo dlhý popis produktu.')}</span>
                        </div> : null}
                    </>
                );

            case 'pricelists':
                let selectedPricelists = [];
                let hasLanguages = false;
                let hasBuyPrice = false;

                _.map(data.product.pricelists, pricelist => {
                    if (subtab === '') {
                        selectedPricelists = [ ...selectedPricelists, pricelist ];
                    }

                    if (pricelist.currency === subtab) {
                        selectedPricelists = [ ...selectedPricelists, pricelist ];

                        if (_.has(pricelist, 'language')) {
                            hasLanguages = true;
                        }

                        if (_.has(pricelist, 'buy_price')) {
                            hasBuyPrice = true;
                        }
                    }
                });

                if (_.isEmpty(selectedPricelists)) {
                    return null;
                }

                return (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{__('Cenník')}</TableCell>
                                {hasLanguages ? <TableCell>{__('Lokalizácia')}</TableCell> : null}
                                <TableCell>{__('Cena bez DPH')}</TableCell>
                                <TableCell>{__('DPH')}</TableCell>
                                <TableCell>{__('Cena s DPH')}</TableCell>
                                {hasBuyPrice ? <TableCell>{__('Nákupná cena')}</TableCell> : null}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {_.map(selectedPricelists, (pricelist, key) => {
                                return (
                                    <TableRow key={key}>
                                        <TableCell>{_.has(pricelist, 'name') ? pricelist.name : __('Predvolené')}</TableCell>
                                        {hasLanguages ? <TableCell>{pricelist.language.toUpperCase()}</TableCell> : null}
                                        <TableCell>{formatAmount(pricelist.price, pricelist.currency)}</TableCell>
                                        <TableCell>{pricelist.tax}%</TableCell>
                                        <TableCell>{formatAmount(pricelist.price_vat, pricelist.currency)}</TableCell>
                                        {hasBuyPrice ? <TableCell>{formatAmount(pricelist.buy_price, pricelist.currency)}</TableCell> : null}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                );

            case 'parameters':
                let selectedParameters = [];

                _.map(data.product.parameters, parameter => {
                    if (subtab === '') {
                        selectedParameters = [ ...selectedParameters, parameter ];
                    }

                    if (parameter.language === subtab) {
                        selectedParameters = [ ...selectedParameters, parameter ];
                    }
                });

                if (_.isEmpty(selectedParameters)) {
                    return null;
                }

                return (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{__('Názov')}</TableCell>
                                <TableCell>{__('Hodnota')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {_.map(selectedParameters, (parameter, key) => {
                                return (
                                    <TableRow key={key}>
                                        <TableCell>{parameter.name}</TableCell>
                                        <TableCell>{parameter.value}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                );

            case 'variants':
                if (_.isEmpty(data.variants)) {
                    return <Message type="info">{__('Produkt nemá varianty')}</Message>;
                }

                return (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{__('Číslo')}</TableCell>
                                <TableCell>{__('EAN')}</TableCell>
                                <TableCell>{__('Stav na sklade')}</TableCell>
                                <TableCell>{__('Váha')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {_.map(data.variants, variant => {
                                return (
                                    <TableRow key={variant.id}>
                                        <TableCell>{variant.number}</TableCell>
                                        <TableCell>{variant.ean}</TableCell>
                                        <TableCell>{variant.stock} {__('ks')}</TableCell>
                                        <TableCell>{variant.weight} {__('kg')}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                );

            case 'related':
                return (
                    <>
                        <div className="input__label">{__('Súvisiace produkty')}</div>
                        {!_.isEmpty(data.related) ? <div className="product-content__panel__tab-content__related">{_.map(data.related, item => {
                            return (
                                <Tooltip title={item.name}>
                                    <div className="product-content__panel__tab-content__related__item" key={item.id}>
                                        {item.img_url !== '' ? <img src={item.img_url} /> : null}
                                    </div>
                                </Tooltip>
                            );
                        })}</div> : null}
                        {_.isEmpty(data.related) ? <div className="product-content__panel__tab-content__related-text">{__('Nie sú zadané')}</div> : null}
                        <div className="input__label">{__('Up-sell produkty')}</div>
                        {!_.isEmpty(data.upsell) ? <div className="product-content__panel__tab-content__related">{_.map(data.upsell, item => {
                            return (
                                <Tooltip title={item.name}>
                                    <div className="product-content__panel__tab-content__related__item" key={item.id}>
                                        {item.img_url !== '' ? <img src={item.img_url} /> : null}
                                    </div>
                                </Tooltip>
                            );
                        })}</div> : null}
                        {_.isEmpty(data.upsell) ? <div className="product-content__panel__tab-content__related-text">{__('Nie sú zadané')}</div> : null}
                        <div className="input__label">{__('Cross-sell produkty')}</div>
                        {!_.isEmpty(data.crosssell) ? <div className="product-content__panel__tab-content__related">{_.map(data.crosssell, item => {
                            return (
                                <Tooltip title={item.name}>
                                    <div className="product-content__panel__tab-content__related__item" key={item.id}>
                                        {item.img_url !== '' ? <img src={item.img_url} /> : null}
                                    </div>
                                </Tooltip>
                            );
                        })}</div> : null}
                        {_.isEmpty(data.crosssell) ? <div className="product-content__panel__tab-content__related-text">{__('Nie sú zadané')}</div> : null}
                    </>
                );
        }

        return null;
    }

    /**
     * Rendrovanie.
     *
     * @return {JSX.Element}
     */
    render() {
        const { data, tab, subtab, languages, currencies, loadingsAi, loadingsSave, loadingsRemove } = this.state;

        if (_.isEmpty(data)) {
            // Data nie su nacitane
            return <div
                className="product-content product-content-loading"
            >{this.renderLoading()}</div>;
        }

        let subtab_content = null;

        const allowChangeTab = _.isEmpty(loadingsAi) && _.isEmpty(loadingsSave) && _.isEmpty(loadingsRemove);

        if (subtab !== '') {
            switch (tab) {
                case 'translations':
                case 'parameters':
                    subtab_content = (
                        <Tabs
                            value={subtab}
                            onChange={allowChangeTab ? (event, subtab) => this.onChangeSubTab(subtab) : () => {}}
                            className="product-content__panel__subtabs"
                        >
                            {_.map(languages, language => <Tab label={language.toUpperCase()} value={language} key={language} />)}
                        </Tabs>
                    );
                    break;

                case 'pricelists':
                    subtab_content = (
                        <Tabs
                            value={subtab}
                            onChange={allowChangeTab ? (event, subtab) => this.onChangeSubTab(subtab) : () => {}}
                            className="product-content__panel__subtabs"
                        >
                            {_.map(currencies, currency => <Tab label={currency.toUpperCase()} value={currency} key={currency} />)}
                        </Tabs>
                    );
                    break;
            }
        }

        return (
            <div className="product-content">
                <div className="product-content__panel">
                    <div className="product-content__panel__photo">
                        {!_.isEmpty(data.product.img_url) ? <img src={data.product.img_url} /> : null}
                    </div>
                    <div className="product-content__panel__photos">
                        {_.map(data.product.images, image => {
                            if (image === data.product.img_url) {
                                return null;
                            }

                            return (
                                <div className="product-content__panel__photos__photo" key={image}>
                                    <img src={image} />
                                </div>
                            );
                        })}
                    </div>
                    <div className="product-content__panel__value">
                        <div className="product-content__panel__value__label">{__('Stav')}:</div>
                        <div className="product-content__panel__value__text">{data.product.active
                            ? <span className="product-content__panel__value__text-active">{__('Aktívny')}</span>
                            : <span className="product-content__panel__value__text-inactive">{__('Neaktívny')}</span>
                        }</div>
                    </div>
                    <div className="product-content__panel__value">
                        <div className="product-content__panel__value__label">{__('Číslo')}:</div>
                        <div className="product-content__panel__value__text">{this.renderValue(data.product.number)}</div>
                    </div>
                    <div className="product-content__panel__value">
                        <div className="product-content__panel__value__label">{__('EAN')}:</div>
                        <div className="product-content__panel__value__text">{this.renderValue(data.product.ean)}</div>
                    </div>
                    <div className="product-content__panel__value">
                        <div className="product-content__panel__value__label">{__('Stav na sklade')}:</div>
                        <div className="product-content__panel__value__text">{data.product.stock} {__('ks')}</div>
                    </div>
                    <div className="product-content__panel__value">
                        <div className="product-content__panel__value__label">{__('Váha')}:</div>
                        <div className="product-content__panel__value__text">{formatAmount(data.product.weight, '')} {__('kg')}</div>
                    </div>
                    <div className="product-content__panel__value">
                        <div className="product-content__panel__value__label">{__('Výrobca')}:</div>
                        <div className="product-content__panel__value__text">{this.renderValue(data.product.manufacturer)}</div>
                    </div>
                    {!_.isEmpty(data.product.categories) ? <div className="product-content__panel__label">{__('Kategórie')}</div> : null}
                    {!_.isEmpty(data.product.categories) ? <div className="product-content__panel__tags">
                        {_.map(data.product.categories, category_id => {
                            if (_.has(data.categories, category_id)) {
                                return <Chip
                                    label={data.categories[category_id]}
                                    key={category_id}
                                    style={{ marginRight: '2px', marginTop: '2px' }}
                                />;
                            }

                            return null;
                        })}
                    </div> : null}
                </div>
                <div className="product-content__panel">
                    <Tabs
                        value={tab}
                        onChange={allowChangeTab ? (event, tab) => this.onChangeTab(tab) : () => {}}
                        className="product-content__panel__tabs"
                    >
                        <Tab label={__('Texty')} value="translations" />
                        <Tab label={__('Cenníky')} value="pricelists" />
                        <Tab label={__('Parametre')} value="parameters" />
                        <Tab label={__('Varianty')} value="variants" />
                        <Tab label={__('Súvisiace')} value="related" />
                    </Tabs>
                    {subtab_content}
                    <div className="product-content__panel__tab-content">
                        {this.renderTab()}
                    </div>
                </div>
                {this.renderSnackbar()}
            </div>
        );
    }
}

export { Product };
